<template>
  <v-container fluid>
    <section v-if="usersAndRolesAllowed && !membershipExpirationOnly">
      <h5 class="wc-h5 mb-4">Modificar estatus del miembro</h5>
      <span>En este apartado podrás cambiar el status del miembro.</span>
      <v-row class="mt-2 mb-8">
        <v-col md="6" sm="12" class="pb-0">
          <v-select
            filled
            dense
            rounded
            label="Nuevo estatus del miembro"
            :items="statusListComputed"
            v-model="selectedStatus"
          >
          </v-select>
        </v-col>
        <v-col cols="12">
          <v-form v-model="membershipTypeSelected">
            <v-row>
              <v-col sm="6" md="6" v-if="selectedStatus == 'ACTIVE'">
                <v-select
                  filled
                  dense
                  rounded
                  persistent-hint
                  v-model="selectedAffiliation"
                  item-text="name"
                  item-value="_id"
                  label="Tipo de membresía"
                  :hint="getPeriodicityLabel()"
                  :rules="[(v) => !!v || 'El tipo de membresía es requerida']"
                  :items="availableAffiliationTypes"
                  :autofocus="true"
                >
                </v-select>
              </v-col>
              <v-col sm="6" md="4" v-if="selectedStatus == 'ACTIVE'">
                <v-text-field
                  v-model="affiliationPrice"
                  type="number"
                  dense
                  label="Monto"
                  filled
                  rounded
                  @keyup="validateMembershipPrice()"
                />
              </v-col>
              <v-col sm="6" md="4" v-if="selectedStatus == 'ACTIVE'">
                <v-menu
                  v-model="datePickerMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="paymentDateFormatted"
                      label="Fecha de pago"
                      prepend-icon="mdi-calendar"
                      readonly
                      filled
                      dense
                      rounded
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="paymentDate"
                    @input="datePickerMenu = false"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col sm="6" md="4" v-if="selectedStatus == 'ACTIVE'">
                <v-menu
                  v-model="inactivationDatePickerMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="inactivationDateFormatted"
                      label="Fecha de inactivación"
                      :hint="membershipExpirationEnabled ? 'Si no cambias esta fecha, se aplicará la fecha fija individual configurada.' : ''"
                      :persistent-hint="membershipExpirationEnabled"
                      prepend-icon="mdi-calendar"
                      readonly
                      filled
                      dense
                      rounded
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="inactivationDate"
                    @input="handleInactivationDateInput"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row v-if="selectedStatus == 'ACTIVE'">
              <v-col cols="12">
                <v-btn
                  elevation="0"
                  :disabled="!selectedAffiliation"
                  class="white--text text-bold rounded-pill"
                  :color="color.primaryVariant"
                  @click="$_handleUpdateUserStatus()"
                >
                  <v-icon class="mr-2"> mdi-content-save </v-icon>
                  Actualizar Estatus
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="selectedStatus != 'ACTIVE' && selectedStatus != ''">
              <v-col cols="12">
                <v-btn
                  elevation="0"
                  class="white--text text-bold rounded-pill"
                  :color="color.primaryVariant"
                  @click="$_handleUpdateUserStatus()"
                >
                  <v-icon class="mr-2"> mdi-content-save </v-icon>
                  Actualizar Estatus
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </section>

    <section
      v-if="usersAndRolesAllowed && (!hideMembershipExpirationConfig || membershipExpirationOnly)"
      class="mb-10"
    >
      <h5 class="wc-h5 mb-2">Fecha fija individual de membresía</h5>
      <span>
        Al activarla, los pagos futuros usarán una fecha de vencimiento propia
        para este perfil y tendrá prioridad sobre la fecha fija del tipo de membresía.
        El vencimiento vigente no cambia.
      </span>
      <v-alert v-if="hasSelectedMembership && !membershipAssignmentSaved" type="info" dense text class="mt-3">
        Guarda primero la membresía asignada para configurar su fecha de renovación.
      </v-alert>
      <v-alert v-else-if="!hasAssignedMembership" type="info" dense text class="mt-3">
        Asigna primero una membresía a este perfil para configurar su fecha de renovación.
      </v-alert>
      <v-alert v-else-if="!membershipExpirationEligible" type="info" dense text class="mt-3">
        Esta opción solo está disponible para membresías anuales o mensuales.
      </v-alert>
      <v-row class="mt-3">
        <v-col cols="12" md="6">
          <v-switch
            v-model="membershipExpirationEnabled"
            label="Usar fecha fija individual"
            :disabled="!membershipExpirationEligible && !membershipExpirationEnabled"
            class="mt-0"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6" v-if="memberRole === 'MEMBER' && membershipExpirationEligible">
          <v-checkbox
            v-model="applyToSubmembers"
            label="Aplicar también a submiembros actuales compatibles"
            hide-details
            class="mt-0"
          />
        </v-col>
        <v-col cols="12" md="6" v-if="membershipExpirationEnabled && membershipExpirationEligible && membershipAssignedPeriod === 'YEAR'">
          <v-select
            v-model="membershipFixedMonth"
            :items="membershipMonthOptions"
            label="Mes de vencimiento"
            filled
            dense
            rounded
          />
        </v-col>
        <v-col cols="12" md="6" v-if="membershipExpirationEnabled && membershipExpirationEligible">
          <v-select
            v-model="membershipFixedDay"
            :items="membershipFixedDayOptions"
            label="Día de vencimiento"
            :disabled="membershipAssignedPeriod === 'YEAR' && !membershipFixedMonth"
            filled
            dense
            rounded
          />
          <span class="text-caption">
            <template v-if="membershipAssignedPeriod === 'YEAR'">
              Se repetirá cada año en el mes y día seleccionados.
            </template>
            <template v-else>
              Se repetirá cada mes. En meses más cortos, se usará su último día.
            </template>
            <template v-if="memberRole === 'MEMBER'">
              Si marcas la opción de submiembros, se copiará a los perfiles actuales con una membresía asignada compatible.
            </template>
          </span>
        </v-col>
        <v-col cols="12" v-if="membershipExpirationEligible || (membershipExpirationConfig && membershipExpirationConfig.enabled)">
          <v-btn
            elevation="0"
            :disabled="savingMembershipExpiration || (membershipExpirationEnabled && (!membershipExpirationEligible || !membershipFixedDay || (membershipAssignedPeriod === 'YEAR' && !membershipFixedMonth)))"
            :loading="savingMembershipExpiration"
            class="white--text text-bold rounded-pill mt-3"
            :color="color.primaryVariant"
            @click="$_handleSaveMembershipExpirationConfig()"
          >
            <v-icon class="mr-2">mdi-content-save</v-icon>
            Guardar fecha fija
          </v-btn>
        </v-col>
      </v-row>
    </section>

    <div v-if="!membershipExpirationOnly">
    <h5 class="wc-h5 mb-4">Gestionar Contraseñas</h5>
    <span> En este apartado podrás restablecer la contraseña del miembro </span>
    <div class="mt-8">
      <v-radio-group
        v-model="passwordRecoverOption"
        class="recover-password-group"
        hide-details
      >
        <v-radio
          label="Cambiar manualmente la contraseña del usuario"
          value="changePasswordManual"
        />
        <v-radio
          label="Enviar enlace por correo para restablecer contraseña"
          value="sendRecoveryEmail"
        />
      </v-radio-group>
    </div>
    <div md="3">
      <v-row no-gutters>
        <v-col cols="12" v-if="passwordRecoverOption == 'changePasswordManual'">
          <v-col md="12">
            <span class="pa-0 ma-0">
              Establecer nueva contraseña para el usuario.
            </span>
          </v-col>
          <v-col sm="12" md="6">
            <v-form v-model="resetPasswordForm">
              <v-text-field
                label="Nueva contraseña del miembro"
                filled
                dense
                rounded
                v-model="newPassword"
                :type="showPass ? 'text' : 'password'"
                :rules="[...passwordRules]"
                :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showPass = !showPass"
              >
              </v-text-field>
            </v-form>
          </v-col>
        </v-col>
        <v-col sm="12" md="12">
          <v-btn
            elevation="0"
            :disabled="
              passwordRecoverOption != 'sendRecoveryEmail'
                ? !resetPasswordForm
                : false
            "
            :color="color.primaryVariant"
            class="rounded-pill text-uppercase text-bold white--text"
            @click="() => (dialogSwitch = true)"
          >
            <!-- <v-icon>
                      mdi-send
                    </v-icon> -->
            {{
              passwordRecoverOption == "changePasswordManual"
                ? "Restablecer contraseña"
                : "Enviar correo"
            }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <personalized-dialog
      :dialog="dialogSwitch"
      @save="$_handleResetPassword()"
      @discard="() => (dialogSwitch = false)"
    >
      <template #alertName>
        <h5>
          {{ confirmationMessages[passwordRecoverOption].title }}
        </h5>
      </template>
      <template #alertContent>
        <span style="font-size: 16px; text-align: center">
          {{ confirmationMessages[passwordRecoverOption].value }}
        </span>
      </template>
    </personalized-dialog>
    </div>

    <admin-submembers-section
      v-if="showSubmembersSection"
      :member-id="memberId"
      :limit-value="submembersLimitValue"
      @updated="$_handleSubmembersUpdated"
    />
  </v-container>
</template>
<script>
import PersonalizedDialog from "./PersonalizedDialog.vue";
import AdminSubmembersSection from "./AdminSubmembersSection.vue";
import { homologateStatusWithColor } from "@/common/hook/useHelper.js";
import { colors } from "@/constants/colors.js";
import { mapMutations, mapState } from "vuex/dist/vuex.common.js";
import { validations } from "@/share/validations";
import { isImefOrganization } from "../../share/imef";
import { isClub51Organization, isOrganization } from "@/share/validateOrganization";
import { orgAllowsSubmembers } from "../../utils/organizationStructure";
import moment from "moment";
import { getInactivationDateCalc, momentDate  } from "../../share/inactivationDateCalc.js";

export default {
  props: {
    memberId: {
      type: String,
      default: "",
    },
    memberEmail: {
      type: String,
      default: "",
    },
    memberStatus: {
      type: String,
      default: "",
    },
    memberRole: {
      type: String,
      default: "",
    },
    membershipExpirationConfig: {
      type: Object,
      default: () => ({ enabled: false, fixedDate: "" }),
    },
    assignedMembership: {
      type: [Object, String],
      default: null,
    },
    membershipAssignmentSaved: {
      type: Boolean,
      default: true,
    },
    hideMembershipExpirationConfig: {
      type: Boolean,
      default: false,
    },
    membershipExpirationOnly: {
      type: Boolean,
      default: false,
    },
    availableAffiliationTypes: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    PersonalizedDialog,
    AdminSubmembersSection,
  },
  data() {
    const savedFixedDate = /^\d{4}-\d{2}-\d{2}$/.test(this.membershipExpirationConfig?.fixedDate || "")
      ? this.membershipExpirationConfig.fixedDate.split('-')
      : null

    return {
      passwordRecoverOption: "changePasswordManual",
      newPassword: "",
      resetPasswordForm: true,
      passwordRules: validations.newPasswordRule(),
      showPass: true,
      color: colors,
      dialogSwitch: false,
      membershipTypeSelected: null,
      selectedStatus: "",
      selectedAffiliation: null,
      affiliationPrice: 0,
      paymentDate: new Date().toISOString().split('T')[0], // Fecha actual por defecto
      inactivationDate: new Date().toISOString().split('T')[0], // Fecha actual por defecto
      inactivationDateOverridden: false,
      datePickerMenu: false,
      inactivationDatePickerMenu: false,
      membershipExpirationEnabled: !!this.membershipExpirationConfig?.enabled,
      membershipFixedMonth: savedFixedDate && this.membershipExpirationConfig?.period !== 'MONTH'
        ? Number(savedFixedDate[1])
        : null,
      membershipFixedDay: savedFixedDate ? Number(savedFixedDate[2]) : null,
      applyToSubmembers: true,
      savingMembershipExpiration: false,
      userStatusList: [
        {
          text: "Activo",
          value: "ACTIVE",
        },
        {
          text: "Inactivo",
          value: "INACTIVO",
        },
        {
          text: "Expirado",
          value: "EXPIRED",
        },
      ],
      periodLabels: {
        YEAR: "Anual",
        MONTH: "Mensual",
        TRIMESTER: "Trimestral",
      },
      confirmationMessages: {
        changePasswordManual: {
          value:
            "Estás a punto de confirmar el restablecimiento de la contraseña. ¿Deseas continuar?",
          title: "Confirmar Restablecimiento de Contraseña",
          url: "/authentication/reset-member-password",
          response:
            "La contraseña del usuario ha sido restablecida correctamente",
        },
        sendRecoveryEmail: {
          value:
            "Estás a punto de confirmar el envío del correo para restablecer la contraseña. ¿Deseas continuar?",
          title: "Confirmar Envío de Correo",
          url: "/authentication/password-recover",
          response:
            "El enlace ha sido enviado al correo del usuario correctamente.",
        },
      },
    };
  },
  methods: {
    ...mapMutations("notification", ["show"]),
    ...mapMutations("loader", ["loading", "loaded"]),
    homologateStatusWithColor,
    isImefOrganization,
    isClub51Organization,
    isOrganization,
    syncMembershipExpirationConfig() {
      const fixedDate = this.membershipExpirationConfig?.fixedDate || "";
      const savedFixedDate = /^\d{4}-\d{2}-\d{2}$/.test(fixedDate)
        ? fixedDate.split('-')
        : null;

      this.membershipExpirationEnabled = !!this.membershipExpirationConfig?.enabled;
      this.membershipFixedMonth = savedFixedDate && this.membershipExpirationConfig?.period !== 'MONTH'
        ? Number(savedFixedDate[1])
        : null;
      this.membershipFixedDay = savedFixedDate ? Number(savedFixedDate[2]) : null;
    },
    $_handleSubmembersUpdated() {
      this.$emit("update");
    },
    disableOptions(statusArray = []) {
      if (this.isClub51Organization(this.user._id, this.user.organization)) {
        this.userStatusList = [
          {
            text: "Baja Temporal",
            value: "TEMPORALLY_LEAVE",
          },
          ...this.userStatusList,
        ];
      }
      if (statusArray.length <= 0) return [...this.userStatusList];

      let disabledStatusArray = [...this.userStatusList].reduce(
        (accum, currentStatus) => {
          if (![...statusArray].includes(currentStatus.value))
            return [...accum, currentStatus];
          return [...accum, { ...currentStatus, disabled: true }];
        },
        []
      );

      return disabledStatusArray;
    },

    async $_handleResetPassword() {
      this.loading();
      try {
        const passwordDirective =
          this.confirmationMessages[this.passwordRecoverOption];
        const payload = {
          memberId: this.memberId,
          updatedPassowrd: this.newPassword,
          email: this.memberEmail,
        };

        const response = await this.axios({
          url: passwordDirective.url,
          method: "POST",
          data: payload,
        });
        if (response.status == 200) {
          this.show({
            text: passwordDirective.response,
            color: "success",
          });
        }
      } catch (error) {
        console.log(error);
        this.show({
          text: "Hubo un error al restablecer la contraseña del usuario, por favor intenta nuevamente",
          color: "error",
        });
      } finally {
        this.newPassword = "";
        this.loaded();
      }
    },

    async $_handleUpdateUserStatus() {
      try {
        const requestData = {
          memberId: this.memberId,
          status: this.selectedStatus,
          membershipId: this.selectedAffiliation,
          total: this.affiliationPrice,
        };

        if (this.selectedStatus === 'ACTIVE') {
          const selectedDate = new Date(this.paymentDate + 'T12:00:00');
          requestData.paymentDate = selectedDate;

          if (this.inactivationDateOverridden) {
            const inactivationDate = new Date(this.inactivationDate);
            requestData.inactivationDate = inactivationDate;
          }
        }

        let response = await this.axios({
          url: "/users/update-status-manually",
          method: "POST",
          data: requestData,
        });
        if (response.status == 200) {
          this.show({
            text: "El estatus del miembro ha sido actualizado exitosamente",
            color: "success",
          });
        }
      } catch (error) {
        const text = error?.response?.data?.error?.message ||
          "Hubo un error al actualizar el estatus del miembro, por favor intenta nuevamente";
        this.show({
          text,
          color: "error",
        });
      } finally {
        this.$emit("update");
        this.resetData();
      }
    },

    async $_handleSaveMembershipExpirationConfig() {
      if (
        this.membershipExpirationEnabled &&
        (!this.membershipExpirationEligible || !this.membershipFixedDay ||
          (this.membershipAssignedPeriod === 'YEAR' && !this.membershipFixedMonth))
      ) return;

      const fixedDate = this.membershipExpirationEligible && this.membershipFixedDay
        ? `2000-${String(this.membershipAssignedPeriod === 'YEAR' ? this.membershipFixedMonth : 1).padStart(2, '0')}-${String(this.membershipFixedDay).padStart(2, '0')}`
        : this.membershipExpirationConfig?.fixedDate || null;
      this.savingMembershipExpiration = true;
      try {
        const response = await this.axios({
          url: `/users/membership-expiration-config/${this.memberId}`,
          method: "PUT",
          data: {
            enabled: this.membershipExpirationEnabled,
            fixedDate,
            applyToSubmembers: this.memberRole === "MEMBER" && this.membershipExpirationEligible && this.applyToSubmembers,
          },
        });
        const count = response.data?.submembersUpdated || 0;
        this.show({
          text: this.applyToSubmembers
            ? `Configuración guardada y aplicada a ${count} submiembro(s) compatible(s)`
            : "Configuración de fecha fija guardada correctamente",
          color: "success",
        });
        this.$emit("update");
      } catch (error) {
        const text = error?.response?.data?.error?.message ||
          "Hubo un error al guardar la fecha fija del miembro";
        this.show({ text, color: "error" });
      } finally {
        this.savingMembershipExpiration = false;
      }
    },

    validateMembershipPrice() {
      this.affiliationPrice = parseInt(this.affiliationPrice);
      if (this.affiliationPrice < 0) {
        this.affiliationPrice = 0;
      }
    },

    findAffiliationById(id = null) {
      const affiliations = [...this.availableAffiliationTypes];

      if (affiliations.length <= 0 || id == null ) return null;

      return affiliations.find(
        (e) => e._id === id
      )
    },

    getAffiliationPrice(currentAffiliation = "") {
      return this.findAffiliationById(currentAffiliation)?.price || 0;
    },

    handleInactivationDateInput() {
      this.inactivationDateOverridden = true;
      this.inactivationDatePickerMenu = false;
    },

    resetData() {
      this.selectedStatus = "";
      this.affiliationPrice = 0;
      this.selectedAffiliation = null;
      this.paymentDate = new Date().toISOString().split('T')[0]; // Reset a fecha actual
      this.inactivationDateOverridden = false;
      this.datePickerMenu = false;
      this.userStatusList = [
        {
          text: "Activo",
          value: "ACTIVE",
        },
        {
          text: "Inactivo",
          value: "INACTIVO",
        },
        {
          text: "Expirado",
          value: "EXPIRED",
        },
      ];
    },

    getPeriodicityLabel() {
      if (!this.selectedAffiliation) return '';

      let label = 'Esta membresía tiene una periodicidad ';
      let staticDate = ''

      const { period = 'YEAR', expirationDatesConfig = null } = this.findAffiliationById(this.selectedAffiliation);

      label += this.periodLabels[period]

      if (!!expirationDatesConfig?.isStaticInactive && period == 'YEAR') {
        staticDate = ' con fecha fija de vencimiento el: ' + moment()
          .month(expirationDatesConfig?.inactivationMonth - 1)
          .date(expirationDatesConfig?.inactivationDay)
          .format(' D [de] MMMM');
      }

      return label + staticDate
    },


    calculateExpirationDate(periodicity) {
      const increments = {
        YEAR: [1, 'year'],
        MONTH: [1, 'month'],
        TRIMESTER: [3, 'months'],
      }

      const [value, unit] = increments[periodicity];
      return moment().add(value, unit).format('D [de] MMMM [de] YYYY');
    }
  },

  watch: {
    memberId() {
      this.syncMembershipExpirationConfig();
      this.applyToSubmembers = true;
    },
    membershipExpirationConfig: {
      deep: true,
      handler() {
        this.syncMembershipExpirationConfig();
      },
    },
    membershipFixedMonth() {
      if (
        this.membershipAssignedPeriod === 'YEAR' &&
        this.membershipFixedDay > this.membershipFixedDayOptions.length
      ) {
        this.membershipFixedDay = this.membershipFixedDayOptions.length || null;
      }
    },
    membershipAssignedPeriod(next, previous) {
      if (
        next === 'YEAR' &&
        previous === 'MONTH' &&
        this.membershipExpirationConfig?.period === 'MONTH'
      ) {
        this.membershipFixedMonth = null;
      }
    },
    selectedAffiliation(next) {
      this.affiliationPrice = this.getAffiliationPrice(next);
      const { period = 'YEAR', expirationDatesConfig = null } = this.findAffiliationById(next)
      const inactivateDate =  getInactivationDateCalc(period, expirationDatesConfig?.inactivationDay, expirationDatesConfig?.inactivationMonth, expirationDatesConfig?.isStaticInactive)

      this.inactivationDate = inactivateDate.toISOString();
      this.inactivationDateOverridden = false;
    },
  },

  computed: {
    ...mapState("user", ["user"]),

    membershipAssignedId() {
      if (!this.assignedMembership) return null;
      return typeof this.assignedMembership === 'object'
        ? this.assignedMembership._id || this.assignedMembership.id || this.assignedMembership.toString()
        : this.assignedMembership;
    },

    membershipAssignedType() {
      const assignedType = this.availableAffiliationTypes.find(
        (type) => String(type._id) === String(this.membershipAssignedId),
      );
      return assignedType || (
        this.assignedMembership && typeof this.assignedMembership === 'object' && this.assignedMembership.period
          ? this.assignedMembership
          : null
      );
    },

    membershipAssignedPeriod() {
      return this.membershipAssignedType?.period || null;
    },

    hasAssignedMembership() {
      return this.hasSelectedMembership && this.membershipAssignmentSaved;
    },

    hasSelectedMembership() {
      return !!this.membershipAssignedId;
    },

    membershipExpirationEligible() {
      return this.hasAssignedMembership && ['YEAR', 'MONTH'].includes(this.membershipAssignedPeriod);
    },

    membershipMonthOptions() {
      return [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
      ].map((text, index) => ({ text, value: index + 1 }));
    },

    membershipFixedDayOptions() {
      const maxDay = this.membershipAssignedPeriod === 'YEAR' && this.membershipFixedMonth
        ? new Date(2000, this.membershipFixedMonth, 0).getDate()
        : 31;
      return Array.from({ length: maxDay }, (_, index) => ({
        text: String(index + 1),
        value: index + 1,
      }));
    },

    // PATCH(IMEF) - Add validation for enable status update section only for HEAD_OFFICE
    usersAndRolesAllowed() {
      const { _id = "", organization = "", role = "" } = this.user;
      let isImef = this.isOrganization( "imef" , _id, organization);

      if(!isImef) return true; // If it's not IMEF, show the section without validating the role

      return (
        ["HEAD_OFFICE"].includes(role)
      );
    },

    isOneLevelOrganization() {
      return this.user.hierarchyType == 'ONE_LEVEL'
    },

    statusListComputed() {
      let listToDisplay = [];
      let statusToBlock = [];
      statusToBlock.push(this.memberStatus);
      switch (this.memberStatus) {
        case "PROSPECTO":
          statusToBlock.push(...["INACTIVO", "EXPIRED"]);
          break;
        case "ACTIVE":
          statusToBlock.push("EXPIRED");
          break;
      }
      console.log(this.memberStatus);
      console.log(statusToBlock);
      listToDisplay = this.disableOptions(statusToBlock);
      return listToDisplay;
    },

    paymentDateFormatted() {
      if (!this.paymentDate) return '';
      const date = new Date(this.paymentDate + 'T12:00:00'); // Evitar problemas de zona horaria
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    inactivationDateFormatted() {
      const date = momentDate(this.inactivationDate)
      this.inactivationDate = date.toISOString();
      return date.toLocalString()
    },

    isRegionalAdmin() {
      const { role = "", permissions = {} } = this.user || {};
      const adminRoles = ['BRANCH', 'HEAD_OFFICE', 'ORGANIZATION', 'ORGANIZATION_USER'];
      if (!adminRoles.includes(role)) return false;
      const membersPerm = (permissions && permissions.Members) || null;
      return !!(membersPerm && membersPerm.update === true);
    },

    organizationAllowsSubmembers() {
      return orgAllowsSubmembers(this.user);
    },

    showSubmembersSection() {
      if (this.membershipExpirationOnly) return false;
      if (this.memberRole !== 'MEMBER') return false;
      if (!this.memberId) return false;
      if (!this.isRegionalAdmin) return false;
      if (!this.organizationAllowsSubmembers) return false;
      return true;
    },

    submembersLimitValue() {
      const assigned = this.membershipAssignedType;
      const raw = assigned && assigned.submembersLimit;
      if (!raw) return 0;
      if (raw.active === false) return 0;
      const value = Number(raw.value || 0);
      return Number.isFinite(value) && value > 0 ? value : 0;
    },

  },
};
</script>
